import { useEffect, useRef } from "react";

import { ArrowDownIcon, ArrowRightIcon, SpinnerIcon } from "@/assets/icons";

import { twMerge } from "tailwind-merge";

type TreeNodeProps<T extends { id: string; name: string }> = {
  node: T;
  depth: number;
  isExpanded: boolean;
  isSelected: boolean;
  isIndeterminate: boolean;
  hasChildren: boolean;
  isLoading?: boolean;
  showCheckbox?: boolean;
  onToggleExpand: (id: string) => void;
  onToggleSelect: (checked: boolean, node: T) => void;
};

const BASE_LABEL_WIDTH = 190; // pixels
export const TREE_INDENT_PER_LEVEL = 20; // pixels

export const TreeNode = <T extends { id: string; name: string }>({
  node,
  depth,
  isExpanded,
  isSelected,
  isIndeterminate,
  hasChildren,
  isLoading,
  showCheckbox = true,
  onToggleExpand,
  onToggleSelect
}: TreeNodeProps<T>) => {
  /** Set indeterminate state on checkbox for partially selected parent nodes */
  const checkboxRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    // Set indeterminate via DOM after mount
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  const chevronIcon = isExpanded ? (
    <ArrowDownIcon className="w-4 h-4 fill-txtBody" />
  ) : (
    <ArrowRightIcon className="w-4 h-4 fill-txtBody" />
  );

  return (
    <div
      className="flex items-center cursor-pointer"
      style={{ paddingLeft: depth * TREE_INDENT_PER_LEVEL }} // indent 20px per level
    >
      {/* Chevron */}
      <button
        aria-label={
          isExpanded ? `Collapse ${node.name}` : `Expand ${node.name}`
        }
        className="w-6 h-6 flex items-center justify-center text-gray-600"
        onClick={() => hasChildren && onToggleExpand(node.id)}
      >
        {hasChildren ? chevronIcon : <div className="w-4 h-4" />}
      </button>
      {/* Checkbox */}
      <div className="w-6 h-6 flex items-center justify-center">
        {showCheckbox && (
          <input
            ref={checkboxRef}
            id={node.id}
            aria-label={node.name}
            type="checkbox"
            className="mr-2 w-3 h-3 accent-primary-teal"
            checked={isSelected}
            onChange={(e) => onToggleSelect(e.target.checked, node)}
          />
        )}
      </div>
      {/* Label */}
      <label
        htmlFor={node.id}
        title={node.name}
        style={{
          width: `calc(${BASE_LABEL_WIDTH}px - ${depth * TREE_INDENT_PER_LEVEL}px)`
        }}
        className={twMerge(
          "text-txtBody text-xs truncate cursor-pointer",
          isLoading && "flex items-center gap-2"
        )}
      >
        {node.name}
        {isLoading && <SpinnerIcon className="spin-animation w-4 h-4" />}
      </label>
    </div>
  );
};
